import React, { useState, useEffect, useMemo } from "react";
import {
    Box, Paper, Typography, TextField, Button, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, MenuItem, Select, FormControl, InputLabel, Grid, Card, CardContent,
    Tooltip, IconButton, Alert
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningIcon from "@mui/icons-material/Warning";
import { API_BASE } from "../../apiBase";

function useAuthHeaders() {
    const token = localStorage.getItem("token");
    return useMemo(() => ({
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
    }), [token]);
}

const CATEGORIES = [
    { value: "", label: "All Categories" },
    { value: "authentication", label: "Authentication" },
    { value: "authorization", label: "Authorization" },
    { value: "data_access", label: "Data Access" },
    { value: "admin_action", label: "Admin Action" },
    { value: "api_usage", label: "API Usage" },
    { value: "configuration", label: "Configuration" },
    { value: "audit", label: "Audit" },
    { value: "security_incident", label: "Security Incident" }
];

const SEVERITIES = [
    { value: "", label: "All Severities" },
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" }
];

function getSeverityColor(severity) {
    switch (severity) {
        case "critical": return "error";
        case "high": return "warning";
        case "medium": return "info";
        case "low": return "success";
        default: return "default";
    }
}

function getCategoryColor(category) {
    switch (category) {
        case "authentication": return "#1976d2";
        case "authorization": return "#9c27b0";
        case "data_access": return "#f57c00";
        case "admin_action": return "#d32f2f";
        case "api_usage": return "#388e3c";
        case "configuration": return "#0288d1";
        case "audit": return "#7b1fa2";
        case "security_incident": return "#c62828";
        default: return "#757575";
    }
}

export default function SecurityLogsSubTab() {
    const headers = useAuthHeaders();

    // Filters
    const [category, setCategory] = useState("");
    const [severity, setSeverity] = useState("");
    const [failedOnly, setFailedOnly] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [limit, setLimit] = useState(100);

    // Data
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [summary, setSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    // Integrity verification
    const [verifying, setVerifying] = useState(false);
    const [integrityResult, setIntegrityResult] = useState(null);

    // Fetch logs
    const fetchLogs = async () => {
        try {
            setLoading(true);
            setError("");

            const params = new URLSearchParams();
            if (category) params.append("category", category);
            if (severity) params.append("severity", severity);
            if (failedOnly) params.append("failed_only", "true");
            if (startDate) params.append("start_date", new Date(startDate).toISOString());
            if (endDate) params.append("end_date", new Date(endDate).toISOString());
            params.append("limit", limit);

            const res = await fetch(`${API_BASE}/admin/security-events?${params.toString()}`, {
                headers
            });

            if (!res.ok) {
                throw new Error(`Failed to fetch logs: ${res.status}`);
            }

            const data = await res.json();
            setLogs(data.events || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch summary
    const fetchSummary = async () => {
        try {
            setLoadingSummary(true);
            const res = await fetch(`${API_BASE}/admin/security-events/summary?days=7`, {
                headers
            });

            if (res.ok) {
                const data = await res.json();
                setSummary(data);
            }
        } catch (err) {
            console.error("Failed to fetch summary:", err);
        } finally {
            setLoadingSummary(false);
        }
    };

    // Verify integrity
    const verifyIntegrity = async () => {
        try {
            setVerifying(true);
            const res = await fetch(`${API_BASE}/admin/security-events/verify-integrity`, {
                method: "POST",
                headers,
                body: JSON.stringify({})
            });

            if (res.ok) {
                const data = await res.json();
                setIntegrityResult(data);
            } else {
                setIntegrityResult({ error: "Failed to verify integrity" });
            }
        } catch (err) {
            setIntegrityResult({ error: err.message });
        } finally {
            setVerifying(false);
        }
    };

    // Load on mount
    useEffect(() => {
        fetchLogs();
        fetchSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Security Event Logs (ISDS A2-4.3.6)
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                Tamper-protected logs of authentication, authorization, data access, and security incidents.
            </Typography>

            {/* Summary Cards */}
            {summary && (
                <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="primary">
                                    {summary.total_events}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total Events (7 days)
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="error">
                                    {summary.failed_events}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Failed Events
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" color="warning.main">
                                    {summary.critical_events}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Critical Events
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <VerifiedIcon color="success" />
                                    <Typography variant="body2" color="text.secondary">
                                        Integrity Protected
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    onClick={verifyIntegrity}
                                    disabled={verifying}
                                    sx={{ mt: 1 }}
                                >
                                    {verifying ? "Verifying..." : "Verify Now"}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Integrity Result */}
            {integrityResult && (
                <Alert
                    severity={integrityResult.integrity_ok ? "success" : "error"}
                    onClose={() => setIntegrityResult(null)}
                    sx={{ mb: 2 }}
                >
                    {integrityResult.error ? (
                        integrityResult.error
                    ) : integrityResult.integrity_ok ? (
                        `✓ Integrity verified: ${integrityResult.verified_count} logs checked, no tampering detected`
                    ) : (
                        `⚠ TAMPERING DETECTED: ${integrityResult.tampered_count} of ${integrityResult.verified_count} logs have been modified!`
                    )}
                </Alert>
            )}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                label="Category"
                            >
                                {CATEGORIES.map(c => (
                                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Severity</InputLabel>
                            <Select
                                value={severity}
                                onChange={(e) => setSeverity(e.target.value)}
                                label="Severity"
                            >
                                {SEVERITIES.map(s => (
                                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Start Date"
                            type="datetime-local"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="End Date"
                            type="datetime-local"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={1}>
                        <TextField
                            label="Limit"
                            type="number"
                            value={limit}
                            onChange={(e) => setLimit(Math.min(1000, Math.max(10, parseInt(e.target.value) || 100)))}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box display="flex" gap={1}>
                            <Button
                                variant="contained"
                                onClick={fetchLogs}
                                disabled={loading}
                                fullWidth
                            >
                                {loading ? <CircularProgress size={20} /> : "Search"}
                            </Button>
                            <Tooltip title="Refresh">
                                <IconButton onClick={fetchSummary} disabled={loadingSummary}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {/* Logs Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Event Type</TableCell>
                            <TableCell>Severity</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Resource</TableCell>
                            <TableCell>IP Address</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Integrity</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No logs found. Adjust filters or wait for events to be logged.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            logs.map((log) => (
                                <TableRow key={log.id} hover>
                                    <TableCell>
                                        <Tooltip title={log.created_at}>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {formatTimestamp(log.created_at)}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.category}
                                            size="small"
                                            sx={{
                                                backgroundColor: getCategoryColor(log.category),
                                                color: 'white',
                                                fontSize: '0.7rem'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {log.event_type}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.severity.toUpperCase()}
                                            size="small"
                                            color={getSeverityColor(log.severity)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {log.username}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {log.resource_type ? `${log.resource_type}${log.resource_id ? `:${log.resource_id}` : ''}` : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                            {log.ip_address || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.success ? "Success" : "Failed"}
                                            size="small"
                                            color={log.success ? "success" : "error"}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {log.integrity_verified ? (
                                            <Tooltip title="Integrity verified">
                                                <VerifiedIcon color="success" fontSize="small" />
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Integrity not verified or tampered">
                                                <WarningIcon color="warning" fontSize="small" />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {logs.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {logs.length} events (limit: {limit})
                    </Typography>
                    {logs.length >= limit && (
                        <Typography variant="body2" color="warning.main">
                            Results limited. Increase limit or refine filters to see more.
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}
